import {
  Anchor,
  Badge,
  Breadcrumbs,
  Center,
  Group,
  Loader,
  Paper,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from "@mantine/core";
import { IconGauge } from "@tabler/icons-react";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";

import { useSiteDevice, useSites } from "@/api/queries";
import type { AdminMeter } from "@/api/types";
import { useAuth } from "@/auth/AuthProvider";
import { getDeviceKindMeta } from "@/components/deviceKinds";
import { ErrorBanner } from "@/components/ErrorBanner";
import { MeterFormModal } from "@/components/MeterFormModal";
import { MeterReadingsTable, type MeterTableRow } from "@/components/MeterReadingsTable";

export function DeviceDetailPage() {
  const { siteSlug, deviceSlug } = useParams<{
    siteSlug: string;
    deviceSlug: string;
  }>();
  const sitesQuery = useSites({ search: siteSlug });
  const deviceQuery = useSiteDevice(siteSlug, deviceSlug);
  const auth = useAuth();
  const isAdmin =
    auth.status === "authenticated" && auth.profile.roles.includes("admin");

  const [editingMeter, setEditingMeter] = useState<AdminMeter | null>(null);

  const site = sitesQuery.data?.items.find((s) => s.slug === siteSlug);
  const device = deviceQuery.data;
  const meta = getDeviceKindMeta(device?.kind ?? "other");
  const Icon = meta.Icon;

  // The /devices detail endpoint returns DeviceMeter rows (no created_at /
  // updated_at, no site_slug, no device_slug); the admin form modal expects
  // AdminMeter. We synthesize the missing fields from the page context —
  // siteSlug + deviceSlug are stable across the device detail view, and
  // the timestamps are not read by the modal itself.
  const startEdit = (row: MeterTableRow) => {
    if (!siteSlug || !deviceSlug) return;
    setEditingMeter({
      id: row.id,
      site_slug: siteSlug,
      device_slug: deviceSlug,
      slug: row.slug,
      display_name: row.display_name,
      kind: row.kind,
      unit: row.unit,
      status: row.status,
      created_at: "",
      updated_at: "",
    });
  };

  return (
    <Stack>
      <Breadcrumbs>
        <Anchor component={Link} to="/sites">
          Sites
        </Anchor>
        <Anchor component={Link} to={`/sites/${siteSlug}`}>
          {site?.display_name ?? siteSlug ?? ""}
        </Anchor>
        <Text>{device?.display_name ?? deviceSlug ?? ""}</Text>
      </Breadcrumbs>

      {deviceQuery.error && <ErrorBanner error={deviceQuery.error} />}

      {deviceQuery.isLoading ? (
        <Center py="xl">
          <Loader />
        </Center>
      ) : device ? (
        <>
          <Group justify="space-between" align="flex-start">
            <Group gap="sm" align="flex-start">
              <ThemeIcon color={meta.color} variant="light" size="xl">
                <Icon size={24} />
              </ThemeIcon>
              <Stack gap={4}>
                <Title order={1}>{device.display_name}</Title>
                <Text c="dimmed" size="sm">
                  {device.site_slug} · {device.slug}
                </Text>
              </Stack>
            </Group>
            <Group gap="xs">
              <Badge variant="light" color={meta.color} tt="lowercase">
                {device.kind}
              </Badge>
              <Badge
                variant="light"
                color={device.status === "active" ? "green" : "gray"}
              >
                {device.status}
              </Badge>
            </Group>
          </Group>

          <Paper withBorder p="lg" radius="md">
            <Group justify="space-between" mb="md">
              <Group gap="xs">
                <IconGauge size={20} />
                <Title order={3}>Meters</Title>
              </Group>
              <Text c="dimmed" size="sm">
                {device.meters.length} meter
                {device.meters.length === 1 ? "" : "s"}
              </Text>
            </Group>

            {device.meters.length > 0 ? (
              <MeterReadingsTable
                meters={device.meters}
                siteSlug={siteSlug}
                showLatest
                onEditMeter={isAdmin ? startEdit : undefined}
              />
            ) : (
              <Text c="dimmed">No meters attached to this device.</Text>
            )}
          </Paper>
        </>
      ) : (
        <Text c="dimmed">Device not found.</Text>
      )}

      <MeterFormModal
        opened={editingMeter !== null}
        onClose={() => setEditingMeter(null)}
        mode="edit"
        meter={editingMeter ?? undefined}
      />
    </Stack>
  );
}
